import m, {MithrilComponent, state} from '@bitstillery/mithril'
import {$t, api, notifier} from '@bitstillery/common/app'
import {FieldText} from '@bitstillery/common/components/fields/text/text'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {Spinner} from '@bitstillery/common/components/ui/spinner/spinner'
import {format_iso_to_date_time} from '@bitstillery/common/lib/format'

import type {AccessTokenListItem, CreateAccessTokenRequest, CreateAccessTokenResponse} from '@bitstillery/common/api/fact2server'

/** Mirrors MAX_ACCESS_TOKENS in common_lib, past which fact2server refuses a create. */
const MAX_ACCESS_TOKENS = 2

interface AccessTokensAttrs {
    /** Only the owner creates: whoever reads a new token can act as the user it belongs to. */
    can_create: boolean
    /** The token collection of one owner, e.g. `portal/user/access-tokens`; each access layer has its own. */
    endpoint: string
}

interface AccessTokensState {
    created: CreateAccessTokenResponse | null
    label: string
    loading: boolean
    saving: boolean
    tokens: AccessTokenListItem[]
}

/**
 * One user's lasting API credentials, where they are created and switched off — the same screen for
 * a Discover user and a portal user, each reached through its own endpoint.
 *
 * A plain table rather than a Collection: a user holds a handful of these, and the point of the
 * screen is the two buttons, not browsing.
 */
export class AccessTokens extends MithrilComponent<AccessTokensAttrs> {
    data: AccessTokensState = state({
        /** The one just made, held only until the screen is left: the server keeps a hash. */
        created: null as CreateAccessTokenResponse | null,
        label: '',
        loading: true,
        saving: false,
        tokens: [] as AccessTokenListItem[],
    })

    endpoint = ''

    override async oninit(vnode: m.Vnode<AccessTokensAttrs>) {
        this.endpoint = vnode.attrs.endpoint
        await this.refresh()
    }

    async refresh() {
        this.data.loading = true
        // The endpoint is the caller's, so no route types the call.
        const {result, success} = await api.get<AccessTokenListItem[]>(this.endpoint)
        this.data.loading = false
        if (!success) {
            notifier.notify($t('access_tokens.load_failed'), 'danger')
            return
        }
        this.data.tokens = result
    }

    async create() {
        this.data.saving = true
        const request: CreateAccessTokenRequest = {label: this.data.label.trim()}
        // The endpoint is the caller's, so no route types the call.
        const {result, success} = await api.post<CreateAccessTokenResponse>(this.endpoint, request, true)
        this.data.saving = false
        if (!success) {
            notifier.notify($t('access_tokens.create_failed'), 'danger')
            return
        }
        this.data.created = result
        this.data.label = ''
        await this.refresh()
    }

    async revoke(row: AccessTokenListItem) {
        const {success} = await api.delete(`${this.endpoint}/${row.artkey}`)
        if (!success) {
            notifier.notify($t('access_tokens.revoke_failed'), 'danger')
            return
        }
        notifier.notify($t('access_tokens.revoked'), 'info')
        await this.refresh()
    }

    async copy(token: string) {
        try {
            await navigator.clipboard.writeText(token)
            notifier.notify($t('access_tokens.copied'), 'info')
        } catch {
            notifier.notify($t('access_tokens.copy_failed'), 'warning')
        }
    }

    view(vnode: m.Vnode<AccessTokensAttrs>): m.Children {
        const {created, loading, saving, tokens} = this.data
        // Revoked tokens are not listed, so every row counts towards the limit.
        const limit_reached = tokens.length >= MAX_ACCESS_TOKENS

        return (
            <div className='c-access-tokens'>
                <p className='intro'>{$t('access_tokens.intro')}</p>

                {vnode.attrs.can_create ? (
                    <div className='create'>
                        <FieldText
                            disabled={limit_reached}
                            label={$t('access_tokens.label')}
                            model={[this.data, 'label']}
                            placeholder={$t('access_tokens.label_placeholder')}
                        />
                        <div className='create-actions'>
                            <Button
                                disabled={saving || loading || limit_reached || !this.data.label.trim()}
                                icon='plus'
                                loading={saving}
                                onclick={() => void this.create()}
                                text={$t('access_tokens.create')}
                                type='info'
                            />
                            {limit_reached && <span className='limit'>{$t('access_tokens.limit_reached')}</span>}
                        </div>
                    </div>
                ) : (
                    <p className='intro'>{$t('access_tokens.owner_only')}</p>
                )}

                {created && (
                    <div className='revealed'>
                        <Icon name='warning' type='warning' />
                        <div className='revealed-body'>
                            <div>{$t('access_tokens.copy_now')}</div>
                            <code className='revealed-value'>{created.token}</code>
                        </div>
                        <Button
                            icon='copy'
                            onclick={() => void this.copy(created.token)}
                            tip={$t('access_tokens.copy')}
                            variant='toggle'
                        />
                        <Button
                            icon='close'
                            onclick={() => (this.data.created = null)}
                            tip={$t('dialog.close')}
                            variant='toggle'
                        />
                    </div>
                )}

                {loading ? (
                    <Spinner />
                ) : (
                    <table className='tokens'>
                        <thead>
                            <tr>
                                <th>{$t('access_tokens.label')}</th>
                                <th>{$t('access_tokens.created_on')}</th>
                                <th>{$t('access_tokens.last_used_on')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map((row) => (
                                <tr key={row.artkey}>
                                    <td className='label'>{row.label}</td>
                                    <td className='date'>{format_iso_to_date_time(row.created_on)}</td>
                                    <td className='date'>{format_iso_to_date_time(row.last_used_on)}</td>
                                    <td className='actions'>
                                        <Button
                                            icon='cancel'
                                            onclick={() => void this.revoke(row)}
                                            size='s'
                                            tip={$t('access_tokens.revoke')}
                                            type='danger'
                                            variant='toggle'
                                        />
                                    </td>
                                </tr>
                            ))}
                            {!tokens.length && (
                                <tr>
                                    <td className='empty' colspan={3}>
                                        {$t('access_tokens.empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        )
    }
}
